import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Trash2, Maximize2, Minimize2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function generateSessionId() {
  return "chat-" + Math.random().toString(36).substring(2, 15);
}

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem("cancun_chat_session");
    if (stored) return stored;
    const id = generateSessionId();
    localStorage.setItem("cancun_chat_session", id);
    return id;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadHistory();
    }
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Don't render on the /assistant page
  if (location.pathname === "/assistant") return null;

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/chat/history/${sessionId}`);
      if (res.data.length > 0) {
        setMessages(res.data.map((m) => ({ role: m.role, content: m.content })));
      }
    } catch {
      // No history
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMsg,
        session_id: sessionId,
        lang: i18n.language,
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.data.response },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: t("chat.error") },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      await axios.delete(`${API}/chat/history/${sessionId}`);
    } catch {
      // ignore
    }
    setMessages([]);
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            data-testid="chat-widget-btn"
            className="fixed bottom-5 left-4 sm:bottom-6 sm:left-6 z-[9999] w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#006D77] text-white shadow-lg hover:bg-[#005a63] transition-colors flex items-center justify-center"
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed z-[9999] bg-white shadow-2xl border border-[#E2E8F0] flex flex-col overflow-hidden
                       inset-x-3 bottom-3 top-20 rounded-2xl
                       sm:inset-x-auto sm:top-auto sm:right-6 sm:bottom-24 sm:w-[380px] sm:h-[520px]"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="bg-[#006D77] text-white px-4 py-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{t("chat.title")}</p>
                  <p className="text-[10px] text-white/70">{t("chat.subtitle")}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => navigate("/assistant")}
                  data-testid="chat-expand-btn"
                  className="w-8 h-8 rounded-lg hover:bg-white/20 flex items-center justify-center transition-colors"
                  title={t("chat.expand")}
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={clearChat}
                  data-testid="chat-clear-btn"
                  className="w-8 h-8 rounded-lg hover:bg-white/20 flex items-center justify-center transition-colors"
                  title={t("chat.clear")}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  data-testid="chat-close-btn"
                  className="w-8 h-8 rounded-lg hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-[#006D77] font-semibold mb-1">{t("chat.welcome")}</p>
                  <p className="text-xs text-[#64748B]">{t("chat.welcome_sub")}</p>
                  <div className="mt-4 space-y-2">
                    {[t("chat.suggestion_1"), t("chat.suggestion_2"), t("chat.suggestion_3")].map((s, i) => (
                      <button
                        key={i}
                        onClick={() => { setInput(s); }}
                        data-testid={`chat-suggestion-${i}`}
                        className="block w-full text-left px-3 py-2 rounded-xl bg-[#006D77]/5 text-sm text-[#1D3557] hover:bg-[#006D77]/10 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    data-testid={`chat-msg-${i}`}
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === "user"
                        ? "bg-[#006D77] text-white rounded-br-sm"
                        : "bg-[#F0F4F8] text-[#1D3557] rounded-bl-sm"
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-[#F0F4F8] px-4 py-3 rounded-2xl rounded-bl-sm">
                    <div className="flex gap-1.5">
                      <span className="w-2 h-2 bg-[#006D77]/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-[#006D77]/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-[#006D77]/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-3 border-t border-[#E2E8F0] shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t("chat.placeholder")}
                  data-testid="chat-input"
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] text-sm text-[#1D3557] focus:outline-none focus:ring-2 focus:ring-[#006D77]/30 focus:border-[#006D77] transition-all"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  data-testid="chat-send-btn"
                  className="w-10 h-10 rounded-full bg-[#006D77] text-white flex items-center justify-center hover:bg-[#005a63] disabled:opacity-40 disabled:hover:bg-[#006D77] transition-colors shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
